import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@material-ui/core";

const DEFAULT_W = 1440;
const DEFAULT_H = 900;

function dcvBaseUrl() {
  const pub = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${window.location.origin}${pub}/dcv-sdk/dcvjs-umd`;
}

function authParamsFromUrl(url) {
  try {
    return new URL(url).searchParams;
  } catch (_) {
    return new URLSearchParams();
  }
}

/** Fit remote viewport into container (same approach as bedrock-agentcore BrowserLiveView). */
export function calculateScale(containerWidth, containerHeight, remoteWidth, remoteHeight) {
  if (containerWidth <= 0 || containerHeight <= 0 || remoteWidth <= 0 || remoteHeight <= 0) {
    return { scale: 1, offsetX: 0 };
  }
  const scale = Math.min(containerWidth / remoteWidth, containerHeight / remoteHeight);
  const offsetX = Math.max(0, (containerWidth - remoteWidth * scale) / 2);
  return { scale, offsetX };
}

function loadDcvSdk() {
  if (window.dcv) return Promise.resolve(window.dcv);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-agentcore-dcv]");
    if (existing) {
      existing.addEventListener("load", () => {
        if (window.dcv) resolve(window.dcv);
        else reject(new Error("DCV SDK loaded but window.dcv missing"));
      });
      existing.addEventListener("error", () => reject(new Error("Failed to load DCV SDK")));
      return;
    }
    const script = document.createElement("script");
    script.src = `${dcvBaseUrl()}/dcv.js`;
    script.async = true;
    script.dataset.agentcoreDcv = "1";
    script.onload = () => {
      if (!window.dcv) {
        reject(new Error("DCV SDK loaded but window.dcv missing"));
        return;
      }
      if (typeof window.dcv.setWorkerPath === "function") {
        try {
          window.dcv.setWorkerPath(`${dcvBaseUrl()}/dcv/`);
        } catch (_) {
          /* baseUrl in connect() covers workers */
        }
      }
      resolve(window.dcv);
    };
    script.onerror = () =>
      reject(new Error(`Failed to load DCV SDK from ${dcvBaseUrl()}/dcv.js`));
    document.head.appendChild(script);
  });
}

/**
 * AgentCore Live View via AWS DCV (cannot iframe the raw signed URL — that returns 501).
 *
 * Important: the DCV canvas is mounted imperatively so React status re-renders
 * do not wipe it (that looked like rapid flicker / white flash).
 */
export default function AgentCoreLiveView({
  signedUrl,
  remoteWidth = DEFAULT_W,
  remoteHeight = DEFAULT_H,
  height = 560,
}) {
  const displayIdRef = useRef(`dcv-display-${Math.random().toString(36).slice(2, 10)}`);
  const shellRef = useRef(null);
  const displayElRef = useRef(null);
  const connectionRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const lastScaleRef = useRef({ scale: null, offsetX: null, cw: 0, ch: 0 });
  const [status, setStatus] = useState({ text: "Connecting…", tone: "loading" });

  const rw = Number(remoteWidth) || DEFAULT_W;
  const rh = Number(remoteHeight) || DEFAULT_H;

  // Create the DCV host node once; never as a React-managed child.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;
    const el = document.createElement("div");
    el.id = displayIdRef.current;
    shell.appendChild(el);
    displayElRef.current = el;
    return () => {
      try {
        el.remove();
      } catch (_) {
        /* ignore */
      }
      displayElRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const displayId = displayIdRef.current;

    const applyScaling = () => {
      const shell = shellRef.current;
      const display = displayElRef.current || document.getElementById(displayId);
      if (!shell || !display) return;
      const { width: w, height: h } = shell.getBoundingClientRect();
      const cw = Math.floor(w);
      const ch = Math.floor(h);
      if (cw <= 0 || ch <= 0) return;

      const { scale, offsetX } = calculateScale(cw, ch, rw, rh);
      const prev = lastScaleRef.current;
      if (
        prev.cw === cw &&
        prev.ch === ch &&
        prev.scale != null &&
        Math.abs(prev.scale - scale) < 0.001 &&
        Math.abs((prev.offsetX || 0) - offsetX) < 0.5
      ) {
        return;
      }
      lastScaleRef.current = { scale, offsetX, cw, ch };

      Object.assign(display.style, {
        width: `${rw}px`,
        height: `${rh}px`,
        transformOrigin: "top left",
        position: "absolute",
        top: "0px",
        left: `${Math.round(offsetX)}px`,
        transform: `scale(${scale})`,
      });
    };

    const run = async () => {
      if (!signedUrl) {
        setStatus({ text: "Missing Live View URL — re-run Preview for a fresh session.", tone: "err" });
        return;
      }
      if (!/^https:\/\//i.test(String(signedUrl))) {
        setStatus({
          text: `Live View URL must be https (got ${String(signedUrl).slice(0, 48)}…)`,
          tone: "err",
        });
        return;
      }

      // Wait a tick so the imperative host div exists.
      await new Promise((r) => requestAnimationFrame(() => r()));
      if (cancelled) return;

      try {
        setStatus({ text: "Loading DCV viewer…", tone: "loading" });
        const dcv = await loadDcvSdk();
        if (cancelled) return;

        setStatus({ text: "Authenticating…", tone: "loading" });
        const credentials = await new Promise((resolve, reject) => {
          dcv.authenticate(String(signedUrl), {
            promptCredentials: (_authType, callback) => callback(null, null),
            httpExtraSearchParams: () => authParamsFromUrl(signedUrl),
            success: (_auth, result) => {
              const creds = result && result[0] ? result[0] : {};
              resolve({
                presignedUrl: String(signedUrl),
                sessionId: creds.sessionId,
                authToken: creds.authToken,
              });
            },
            error: (_auth, error) => {
              reject(new Error(`DCV auth failed: ${JSON.stringify(error)}`));
            },
          });
        });
        if (cancelled) return;

        setStatus({ text: "Connecting to browser session…", tone: "loading" });
        const connection = await dcv.connect({
          url: credentials.presignedUrl,
          sessionId: credentials.sessionId,
          authToken: credentials.authToken,
          divId: displayId,
          baseUrl: dcvBaseUrl(),
          observers: {
            httpExtraSearchParams: () => authParamsFromUrl(credentials.presignedUrl),
            firstFrame: () => {
              if (cancelled) return;
              setStatus({
                text: "Live — interact here (view is scaled to fit), then click Continue.",
                tone: "ok",
              });
              applyScaling();
            },
            error: (error) => {
              if (!cancelled) {
                setStatus({
                  text: `Connection error: ${JSON.stringify(error)}`,
                  tone: "err",
                });
              }
            },
            close: () => {
              if (!cancelled) setStatus({ text: "Disconnected", tone: "err" });
            },
          },
        });
        if (cancelled) {
          try {
            connection.disconnect && connection.disconnect();
          } catch (_) {
            /* ignore */
          }
          return;
        }
        connectionRef.current = connection;

        // Lock remote layout ONCE. Do not call this from applyScaling — that
        // retriggers displayLayout and flickers the stream.
        try {
          connection.requestDisplayLayout?.([
            {
              name: "Main Display",
              rect: { x: 0, y: 0, width: rw, height: rh },
              primary: true,
            },
          ]);
        } catch (_) {
          /* ignore */
        }

        applyScaling();

        if (shellRef.current && typeof ResizeObserver !== "undefined") {
          let resizeTimer = null;
          resizeObserverRef.current = new ResizeObserver(() => {
            if (resizeTimer) clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
              if (!cancelled) applyScaling();
            }, 150);
          });
          resizeObserverRef.current.observe(shellRef.current);
        }
      } catch (e) {
        if (!cancelled) {
          setStatus({
            text: e?.message || String(e),
            tone: "err",
          });
        }
      }
    };

    run();
    return () => {
      cancelled = true;
      try {
        resizeObserverRef.current?.disconnect();
      } catch (_) {
        /* ignore */
      }
      resizeObserverRef.current = null;
      try {
        if (connectionRef.current && connectionRef.current.disconnect) {
          connectionRef.current.disconnect();
        }
      } catch (_) {
        /* ignore */
      }
      connectionRef.current = null;
    };
  }, [signedUrl, rw, rh]);

  const toneColor =
    status.tone === "ok" ? "#1b5e20" : status.tone === "err" ? "#b71c1c" : "#e65100";
  const toneBg =
    status.tone === "ok" ? "#e8f5e9" : status.tone === "err" ? "#ffebee" : "#fff8e1";

  const resolvedHeight =
    height == null || height === ""
      ? "min(62vh, 640px)"
      : typeof height === "number"
        ? `${height}px`
        : String(height);

  return (
    <Box
      style={{
        width: "100%",
        height: resolvedHeight,
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.12)",
        backgroundColor: "#111",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        style={{
          flex: "0 0 auto",
          padding: "8px 12px",
          backgroundColor: toneBg,
          color: toneColor,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="caption" style={{ color: toneColor, fontWeight: 600 }}>
          {status.text}
        </Typography>
      </Box>
      <Box
        ref={shellRef}
        style={{
          flex: 1,
          minHeight: 0,
          backgroundColor: "#000",
          position: "relative",
          overflow: "hidden",
        }}
      />
    </Box>
  );
}

export function buildLiveViewPageUrl(signedUrl, remoteWidth, remoteHeight) {
  const w = Number(remoteWidth) || DEFAULT_W;
  const h = Number(remoteHeight) || DEFAULT_H;
  const qs = new URLSearchParams({
    url: String(signedUrl || ""),
    w: String(w),
    h: String(h),
  });
  const pub = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${pub}/agentcore-live-view?${qs.toString()}`;
}
